/**
 * @voltxampere/ui — Wire vibration overlay (Sprint 58)
 *
 * Sister to {@link drawWireOverlay} (Sprint 13). Paints the wire
 * path with a sinüsoid perpendicular perturbation so high-current
 * wires read as "buzzing". Sits on the fx overlay canvas alongside
 * the meteor particles (Sprint 29) so the base wire stroke from
 * `draw/wire.ts` stays untouched.
 *
 * Amplitude is supplied per-wire by the polish-effects store
 * (Sprint 57) — `amplitude ∈ [0, 1]` where 0 is just-above-threshold
 * and 1 is the saturation current. The caller multiplies by a
 * pixel-amplitude constant before invoking; this helper takes the
 * absolute pixel value to keep the rendering layer locale-free of
 * threshold semantics.
 *
 * `phase` is shared across all wires so the overall "ripple" is
 * coherent — coordinated buzz rather than per-wire chaos. The
 * caller advances phase from a rAF loop; this helper is pure draw.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/**
 * Draw a perturbed copy of `path` onto `ctx`. The caller has already
 * set strokeStyle / lineWidth (or relies on the helper's defaults).
 * The helper saves/restores its own state so callers can chain it
 * with other overlay strokes.
 */
export function drawWireVibration(ctx, opts, theme) {
    const { path, amplitudePx, phase } = opts;
    if (path.length < 2 || amplitudePx <= 0)
        return;
    const wavelengthCycles = opts.wavelengthCycles ?? 1.4;
    const samplesPerSegment = opts.samplesPerSegment ?? 6;
    ctx.save();
    ctx.strokeStyle = theme.accent.brassHot;
    ctx.lineWidth = 1.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.globalAlpha = 0.85;
    ctx.beginPath();
    // Compute segment lengths + total length so the wave frequency
    // along arc length is uniform regardless of segment kinks.
    const segLens = [];
    let total = 0;
    for (let i = 1; i < path.length; i++) {
        const a = path[i - 1];
        const b = path[i];
        if (a === undefined || b === undefined)
            continue;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const len = Math.hypot(dx, dy);
        segLens.push(len);
        total += len;
    }
    if (total === 0) {
        ctx.restore();
        return;
    }
    const omega = (wavelengthCycles * 2 * Math.PI) / total;
    let arcAccum = 0;
    let first = true;
    for (let i = 1; i < path.length; i++) {
        const a = path[i - 1];
        const b = path[i];
        const segLen = segLens[i - 1] ?? 0;
        if (a === undefined || b === undefined || segLen === 0)
            continue;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        // Unit perpendicular = rotate (dx, dy) by 90° → (-dy, dx) / len.
        const nx = -dy / segLen;
        const ny = dx / segLen;
        for (let s = first ? 0 : 1; s <= samplesPerSegment; s++) {
            const t = s / samplesPerSegment;
            const px = a.x + dx * t;
            const py = a.y + dy * t;
            const arc = arcAccum + segLen * t;
            const offset = Math.sin(omega * arc + phase) * amplitudePx;
            const ox = px + nx * offset;
            const oy = py + ny * offset;
            if (first) {
                ctx.moveTo(ox, oy);
                first = false;
            }
            else {
                ctx.lineTo(ox, oy);
            }
        }
        arcAccum += segLen;
    }
    ctx.stroke();
    ctx.restore();
}
//# sourceMappingURL=wire-vibration.js.map